import * as moment from 'moment';

declare const Helpers: any;

/** Returns the absolute deviation value for display (always positive). */
export function getPositiveDeviationValue(value: any): any {
  if (value == 0 || value < 0) {
    return -(value);
  }
  return value;
}

/** Returns whether a deviation should be shown in tables and charts (hides zero / no-change). */
export function shouldShowDeviation(deviation: any): boolean {
  if (!deviation?.arrowType || deviation.arrowType === 'no-change') {
    return false;
  }
  const rawDiff = deviation.difference ?? deviation.deviation;
  if (rawDiff === 0 || rawDiff === '0' || rawDiff === '0.0') {
    return false;
  }
  return true;
}

/** Returns whether period-over-period comparison is allowed for the current date selection. */
export function isComparisonAvailable(): boolean {
  return !Helpers?.datePicker?.isCustomDateRangeSelected;
}

/** Shared across score tabs so "Show Comparison" stays checked when switching tabs. */
export function getShowComparisonFlag(): boolean {
  return !!Helpers?.showScoreComparisonFlag;
}

/** Persists the Show Comparison checkbox state for the current score session. */
export function setShowComparisonFlag(checked: boolean): void {
  if (typeof Helpers !== 'undefined') {
    Helpers.showScoreComparisonFlag = !!checked;
  }
}

/** Builds a human-readable label for the previous comparison period from the active date range. */
export function getComparisonPeriodLabel(): string {
  const datePicker = Helpers?.datePicker;
  if (!datePicker || datePicker.isCustomDateRangeSelected) {
    return '';
  }

  const startDate = moment(datePicker.startDate, 'YYYYMMDD');
  if (!startDate.isValid()) {
    return '';
  }

  const trendType = datePicker.trendType || getTrendTypeFromSelectedTrend(datePicker.selectedTrend);

  switch (trendType) {
    case 'day':
      return startDate.clone().subtract(1, 'day').format('DD MMM YYYY');
    case 'week':
      return 'last week';
    case 'month':
      return startDate.clone().subtract(1, 'month').format('MMM YYYY');
    case 'quarter': {
      const prevQuarterStart = startDate.clone().subtract(1, 'quarter').startOf('quarter');
      const prevQuarterEnd = prevQuarterStart.clone().endOf('quarter');
      return `${prevQuarterStart.format('MMM')}-${prevQuarterEnd.format('MMM YYYY')}`;
    }
    case 'year':
      return startDate.clone().subtract(1, 'year').format('YYYY');
    case 'financial_year':
      return getFinancialYearStart(startDate).clone().subtract(1, 'year').format('YYYY');
    default:
      return '';
  }
}

/** Tooltip text for deviation values in score tables (e.g. "vs last week"). */
export function getDeviationTooltipText(deviation: any, comparisonPeriodLabel: string): string {
  if (comparisonPeriodLabel) {
    return `vs ${comparisonPeriodLabel}`;
  }
  return deviation?.deviation ?? '';
}

function getTrendTypeFromSelectedTrend(selectedTrend: number): string {
  switch (selectedTrend) {
    case 1: return 'day';
    case 2: return 'week';
    case 3: return 'month';
    case 4: return 'quarter';
    case 5: return 'year';
    default: return 'month';
  }
}

function getFinancialYearStart(startDate: moment.Moment): moment.Moment {
  if (startDate.quarter() > 1) {
    return startDate.clone().quarter(2).startOf('quarter');
  }
  return startDate.clone().subtract(1, 'years').quarter(2).startOf('quarter');
}

/** Extracts a primary rating distribution histogram from a nested or flat API node. */
export function pickPrimaryRatingDistributionFromNode(node: any): Record<string, number> | null {
  if (node == null || typeof node !== 'object' || Array.isArray(node)) {
    return null;
  }
  const nested = node.primary_rating_distribution ?? node.primary_rating_distributions;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, number>;
  }
  if (nodeLooksLikeDistributionHistogram(node)) {
    return node as Record<string, number>;
  }
  return null;
}

/** Checks whether an object looks like a numeric rating histogram keyed by score values. */
function nodeLooksLikeDistributionHistogram(node: Record<string, any>): boolean {
  const keys = Object.keys(node);
  if (keys.length === 0) {
    return false;
  }
  return keys.every(k => /^-?\d+$/.test(String(k)));
}

/** Resolves current and previous primary rating distributions from a comparison API entry. */
export function extractComparisonDistributionsFromRatingEntry(entry: any): {
  current: Record<string, number> | null;
  previous: Record<string, number> | null;
} {
  if (!entry) {
    return { current: null, previous: null };
  }
  const fromCurrentWrapper = pickPrimaryRatingDistributionFromNode(entry.current);
  const fromPreviousWrapper = pickPrimaryRatingDistributionFromNode(entry.previous);
  const fromFlat = pickPrimaryRatingDistributionFromNode(entry);
  const fromFlatPrevious =
    entry.previous_primary_rating_distribution != null
      ? pickPrimaryRatingDistributionFromNode({ primary_rating_distribution: entry.previous_primary_rating_distribution })
      : entry.previous_primary_rating_distributions != null
        ? pickPrimaryRatingDistributionFromNode({ primary_rating_distribution: entry.previous_primary_rating_distributions })
        : null;
  if (fromCurrentWrapper != null || fromPreviousWrapper != null) {
    return {
      current: fromCurrentWrapper ?? fromFlat,
      previous: fromPreviousWrapper ?? fromFlatPrevious,
    };
  }
  return { current: fromFlat, previous: fromFlatPrevious };
}

/** Ensures a table row has a deviations map and returns it for in-place updates. */
function ensureRowDeviations(row: any): Record<string, any> {
  if (!row.deviations) {
    row.deviations = {};
  }
  return row.deviations;
}

/** Attaches a single column deviation on a row using Helpers.getFieldDeviation. */
function attachNumericColumnDeviation(row: any, columnKey: string | number | undefined, current: number, previous: number): void {
  if (!row || columnKey == null || columnKey === '' || typeof Helpers === 'undefined' || !Helpers.getFieldDeviation) {
    return;
  }
  if (current == null || isNaN(current)) {
    return;
  }
  const deviation = Helpers.getFieldDeviation(current, previous ?? 0);
  if (shouldShowDeviation(deviation)) {
    ensureRowDeviations(row)[columnKey] = deviation;
  }
}

/** Computes weightage percentage for a row from its response count and overall total. */
export function computeWeightagePercent(responses: number, overallTotal: number): number {
  if (!overallTotal || overallTotal <= 0 || !responses) {
    return 0;
  }
  const pct = (responses / overallTotal) * 100;
  const fixed = pct.toFixed(1);
  return fixed.endsWith('.0') ? Math.floor(pct) : parseFloat(fixed);
}

/** Attaches weightage column deviation by comparing current vs previous period percentages. */
export function attachWeightageDeviation(
  row: any,
  currentResponses: number,
  previousResponses: number,
  currentOverallTotal: number,
  previousOverallTotal: number,
  columnKey: string | number = 'weightage'
): void {
  const currentWeightage = computeWeightagePercent(currentResponses, currentOverallTotal);
  const previousWeightage = computeWeightagePercent(previousResponses, previousOverallTotal);
  attachNumericColumnDeviation(row, columnKey, currentWeightage, previousWeightage);
}

/** Reads current and previous detailed_rating_by_type results for a field from a comparison bucket. */
export function getComparisonDetailedRatingResult(
  bucket: any,
  fieldId: string
): { current: any; previous: any } {
  if (!bucket || !fieldId) {
    return { current: null, previous: null };
  }
  const current =
    bucket?.detailed_rating_by_type?.[fieldId] ??
    bucket?.current?.detailed_rating_by_type?.[fieldId] ??
    null;
  const previous = bucket?.previous?.detailed_rating_by_type?.[fieldId] ?? null;
  return { current, previous };
}

/** Converts a flat primary_rating_distribution object into a rating_number + doc_count shape. */
export function shapeDistributionAsRatingResult(dist: Record<string, number> | null | undefined): any {
  if (!dist || typeof dist !== 'object') {
    return null;
  }
  let doc_count = 0;
  const rating_number: Record<string, number> = {};
  for (const key in dist) {
    if (key === 'doc_count_error_upper_bound' || key === 'sum_other_doc_count') {
      continue;
    }
    const count = Number(dist[key]) || 0;
    rating_number[key] = count;
    doc_count += count;
  }
  return { rating_number, doc_count };
}

/** Attaches per-option count deviations for single-select comparison rows. */
export function attachSelectionOptionDeviations(
  row: any,
  currentResult: any,
  previousResult: any,
  prop: Record<string, { value?: any; label?: string }> | null | undefined
): void {
  if (!prop) {
    return;
  }
  for (const propKey in prop) {
    const columnId = prop[propKey]?.value ?? propKey;
    const currentCount =
      currentResult?.rating_number?.[propKey] ??
      currentResult?.rating_number?.[columnId] ??
      0;
    const previousCount =
      previousResult?.rating_number?.[propKey] ??
      previousResult?.rating_number?.[columnId] ??
      0;
    attachNumericColumnDeviation(row, columnId, currentCount, previousCount);
    const numericColumnId = Number(propKey);
    if (
      !isNaN(numericColumnId) &&
      numericColumnId !== columnId &&
      row[numericColumnId] !== undefined
    ) {
      attachNumericColumnDeviation(row, numericColumnId, currentCount, previousCount);
    }
  }
}

/** Aggregates top, mid, and bottom band counts from a detailed rating result histogram. */
export function getBandCountsFromRatingResult(
  data: any,
  scalerRaterType: string,
  getRatingBands: (type: string) => any
): { top: number; mid: number; bottom: number } | null {
  if (!data) {
    return null;
  }
  let totalTop = 0;
  let totalMid = 0;
  let totalBottom = 0;
  const category = getRatingBands(scalerRaterType);
  const ratingNumber = data?.rating_number || {};
  for (const key in ratingNumber) {
    const numberOfUsersRated = ratingNumber[key] || 0;
    const numericKey = Number(key);
    if (category?.bottom?.includes(numericKey)) {
      totalBottom += numberOfUsersRated;
    } else if (category?.mid?.includes(numericKey)) {
      totalMid += numberOfUsersRated;
    } else if (category?.top?.includes(numericKey)) {
      totalTop += numberOfUsersRated;
    }
  }
  return { top: totalTop, mid: totalMid, bottom: totalBottom };
}

/** Reads promoter/passive/detractor (or top/mid/bottom) counts from an account summary node. */
export function getAccountBandMetrics(summaryData: any): { top: number; mid: number; bottom: number } | null {
  if (!summaryData) {
    return null;
  }
  return {
    top: summaryData?.promoters || summaryData?.top || summaryData?.positives || 0,
    mid: summaryData?.passives || summaryData?.mid || 0,
    bottom: summaryData?.detractors || summaryData?.bottom || summaryData?.negatives || 0,
  };
}

/** Attaches top, mid, and bottom band deviations on a row from current vs previous band counts. */
export function attachBandDeviations(
  row: any,
  currentBands: { top: number; mid: number; bottom: number },
  previousBands: { top: number; mid: number; bottom: number },
  columnMap: { top?: string; mid?: string; bottom?: string } = { top: 'top', mid: 'mid', bottom: 'bottom' },
  includeMid = true
): void {
  attachNumericColumnDeviation(row, columnMap.top, currentBands.top, previousBands.top ?? 0);
  if (includeMid) {
    attachNumericColumnDeviation(row, columnMap.mid, currentBands.mid, previousBands.mid ?? 0);
  }
  attachNumericColumnDeviation(row, columnMap.bottom, currentBands.bottom, previousBands.bottom ?? 0);
}

/** Attaches rating, response, and band deviations for a detailed rating comparison row. */
export function attachRatingResultDeviations(
  row: any,
  currentResult: any,
  previousResult: any,
  scalerRaterType: string,
  getRatingBands: (type: string) => any,
  columnMap: {
    rating?: string;
    responses?: string;
    top?: string;
    mid?: string;
    bottom?: string;
  } = { rating: 'rating', responses: 'responses', top: 'top', mid: 'mid', bottom: 'bottom' }
): void {
  attachMetricDeviations(
    row,
    getNumericMetricsFromRatingResult(currentResult, scalerRaterType, getRatingBands),
    getNumericMetricsFromRatingResult(previousResult, scalerRaterType, getRatingBands),
    columnMap
  );
  const currentBands = getBandCountsFromRatingResult(currentResult, scalerRaterType, getRatingBands);
  if (!currentBands) {
    return;
  }
  const previousBands = getBandCountsFromRatingResult(previousResult, scalerRaterType, getRatingBands) ?? {
    top: 0,
    mid: 0,
    bottom: 0,
  };
  const category = getRatingBands(scalerRaterType);
  const includeMid = !!(category?.mid && category.mid.length);
  attachBandDeviations(row, currentBands, previousBands, columnMap, includeMid);
}

/** Attaches rating and response count deviations on a row from numeric current vs previous metrics. */
export function attachMetricDeviations(
  row: any,
  currentMetrics: { rating?: number; responses?: number } | null,
  previousMetrics: { rating?: number; responses?: number } | null,
  columnMap: { rating?: string; responses?: string } = { rating: 'rating', responses: 'responses' }
): void {
  if (!row || typeof Helpers === 'undefined' || !Helpers.getFieldDeviation) {
    return;
  }
  const cur = currentMetrics ?? { rating: 0, responses: 0 };
  const prev = previousMetrics ?? { rating: 0, responses: 0 };
  const ratingDev = Helpers.getFieldDeviation(cur.rating ?? 0, prev.rating ?? 0);
  const responseDev = Helpers.getFieldDeviation(cur.responses ?? 0, prev.responses ?? 0);
  if (shouldShowDeviation(ratingDev) && columnMap.rating) {
    ensureRowDeviations(row)[columnMap.rating] = ratingDev;
  }
  if (shouldShowDeviation(responseDev) && columnMap.responses) {
    ensureRowDeviations(row)[columnMap.responses] = responseDev;
  }
}

/** Attaches rating and response deviations derived from primary rating distribution histograms. */
export function attachRatingResponseDeviations(
  row: any,
  currentDist: any,
  previousDist: any,
  calculateFromDistribution: (dist: Record<string, number> | null | undefined) => { rating: number; responses: number } | null
): void {
  const cur = calculateFromDistribution(currentDist) ?? { rating: 0, responses: 0 };
  const prev = calculateFromDistribution(previousDist) ?? { rating: 0, responses: 0 };
  attachMetricDeviations(row, cur, prev);
}

/** Resolves current and previous summary nodes from a comparison API entry. */
export function extractComparisonSummaryNodesFromRatingEntry(entry: any): {
  current: any;
  previous: any;
} {
  if (!entry) {
    return { current: null, previous: null };
  }
  if (entry.current !== undefined || entry.previous !== undefined) {
    return {
      current: entry.current ?? null,
      previous: entry.previous ?? null,
    };
  }
  return {
    current: entry,
    previous: entry.previous ?? null,
  };
}

function hasComparisonSummaryFields(summary: any): boolean {
  if (!summary) {
    return false;
  }
  return (
    summary.number_of_users_rated_primary_rating != null ||
    summary.users_rater_primary_rating != null ||
    summary.doc_count != null ||
    summary.promoters != null ||
    summary.net_promoter_score != null ||
    summary.average_rating != null ||
    summary.sum_primary_rating != null ||
    pickPrimaryRatingDistributionFromNode(summary) != null
  );
}

/** Resolves current and previous comparison metrics using summary fields, then distribution fallback. */
export function resolveComparisonMetrics(
  entry: any,
  currentSummary: any,
  previousSummary: any,
  isNps: boolean,
  calculateFromDistribution?: (dist: Record<string, number> | null | undefined) => { rating: number; responses: number } | null
): {
  current: { rating: number; responses: number } | null;
  previous: { rating: number; responses: number } | null;
} {
  const summaryNodes = extractComparisonSummaryNodesFromRatingEntry(entry);
  const currentNode = currentSummary ?? summaryNodes.current;
  const previousNode = previousSummary ?? summaryNodes.previous;
  const { current: currentDist, previous: previousDist } = extractComparisonDistributionsFromRatingEntry(entry);
  const currentMetrics =
    getAccountSummaryMetrics(currentNode, isNps, calculateFromDistribution) ??
    (calculateFromDistribution ? calculateFromDistribution(currentDist) : null);
  const previousMetrics =
    getAccountSummaryMetrics(previousNode, isNps, calculateFromDistribution) ??
    (calculateFromDistribution ? calculateFromDistribution(previousDist) : null);
  return { current: currentMetrics, previous: previousMetrics };
}

/** Attaches comparison deviations from API summary nodes, with distribution fallback. */
export function attachComparisonEntryDeviations(
  row: any,
  entry: any,
  isNps: boolean,
  calculateFromDistribution?: (dist: Record<string, number> | null | undefined) => { rating: number; responses: number } | null
): void {
  const { current, previous } = extractComparisonSummaryNodesFromRatingEntry(entry);
  const { current: currentMetrics, previous: previousMetrics } = resolveComparisonMetrics(
    entry,
    current,
    previous,
    isNps,
    calculateFromDistribution
  );
  attachMetricDeviations(
    row,
    currentMetrics ?? { rating: 0, responses: 0 },
    previousMetrics ?? { rating: 0, responses: 0 }
  );
}

/** Parses rating and response counts from a comparison summary node for NPS or average-score types. */
export function getAccountSummaryMetrics(
  summaryData: any,
  isNps: boolean,
  calculateFromDistribution?: (dist: Record<string, number> | null | undefined) => { rating: number; responses: number } | null
): { rating: number; responses: number } | null {
  if (!summaryData) {
    return null;
  }
  const responses =
    summaryData?.number_of_users_rated_primary_rating ??
    summaryData?.users_rater_primary_rating ??
    summaryData?.doc_count ??
    0;
  if (isNps) {
    if (summaryData.net_promoter_score != null && summaryData.net_promoter_score !== 'N/A') {
      const nps = Number(summaryData.net_promoter_score);
      if (!isNaN(nps)) {
        return { rating: Math.round(nps), responses };
      }
    }
    const top = summaryData?.promoters || summaryData?.top || summaryData?.positives || 0;
    const bottom = summaryData?.detractors || summaryData?.bottom || summaryData?.negatives || 0;
    if (responses > 0 && (top > 0 || bottom > 0)) {
      let rating: any = ((top - bottom) / responses) * 100;
      if (rating < 0 && (rating % 1 === -0.5 || rating % 1 === 0.5)) {
        rating = Math.floor(rating);
      } else if (rating > 0 && rating % 1 === 0.5) {
        rating = Math.ceil(rating);
      } else {
        rating = Math.round(rating);
      }
      return { rating, responses };
    }
  } else {
    if (summaryData.average_rating != null && summaryData.average_rating !== 'N/A') {
      return {
        rating: parseFloat(Number(summaryData.average_rating).toFixed(2)),
        responses,
      };
    }
    if (responses > 0 && summaryData.sum_primary_rating != null) {
      return {
        rating: parseFloat((Number(summaryData.sum_primary_rating) / responses).toFixed(2)),
        responses,
      };
    }
  }
  if (calculateFromDistribution) {
    const metrics = calculateFromDistribution(pickPrimaryRatingDistributionFromNode(summaryData));
    if (metrics) {
      return metrics;
    }
  }
  return { rating: 0, responses };
}

/** Attaches full account-summary deviations including rating, responses, bands, and optional weightage. */
export function attachSummaryDeviations(
  row: any,
  currentSummary: any,
  previousSummary: any,
  isNps: boolean,
  columnMap: {
    rating?: string;
    responses?: string;
    top?: string;
    mid?: string;
    bottom?: string;
  } = { rating: 'rating', responses: 'responses', top: 'top', mid: 'mid', bottom: 'bottom' },
  weightageTotals?: { currentOverallTotal: number; previousOverallTotal: number }
): void {
  if (!row || (!currentSummary && !previousSummary)) {
    return;
  }
  attachMetricDeviations(
    row,
    getAccountSummaryMetrics(currentSummary, isNps),
    getAccountSummaryMetrics(previousSummary, isNps),
    columnMap
  );
  const currentBands = currentSummary
    ? (getAccountBandMetrics(currentSummary) ?? { top: 0, mid: 0, bottom: 0 })
    : { top: 0, mid: 0, bottom: 0 };
  const previousBands = previousSummary
    ? (getAccountBandMetrics(previousSummary) ?? { top: 0, mid: 0, bottom: 0 })
    : { top: 0, mid: 0, bottom: 0 };
  const includeMid = isNps || currentBands.mid > 0 || previousBands.mid > 0;
  attachBandDeviations(row, currentBands, previousBands, columnMap, includeMid);
  if (weightageTotals) {
    const currentResponses =
      currentSummary?.users_rater_primary_rating || currentSummary?.doc_count || 0;
    const previousResponses =
      previousSummary?.users_rater_primary_rating || previousSummary?.doc_count || 0;
    attachWeightageDeviation(
      row,
      currentResponses,
      previousResponses,
      weightageTotals.currentOverallTotal,
      weightageTotals.previousOverallTotal
    );
  }
}

/** Computes numeric rating and response totals from a cross-tab detailed rating result. */
export function getNumericMetricsFromRatingResult(data: any, scalerRaterType: string, getRatingBands: (type: string) => any): { rating: number; responses: number } | null {
  if (!data) {
    return null;
  }
  let totalResponse: number = data?.doc_count || 0;
  if (!totalResponse) {
    return null;
  }
  let totalTop = 0;
  let totalMid = 0;
  let totalBottom = 0;
  let sumOfPrimaryRating = 0;
  const category = getRatingBands(scalerRaterType);
  const ratingNumber = data?.rating_number || {};
  for (const key in ratingNumber) {
    const numberOfUsersRated = ratingNumber[key] || 0;
    const numericKey = Number(key);
    if (category.bottom?.includes(numericKey)) {
      totalBottom += numberOfUsersRated;
      sumOfPrimaryRating += numberOfUsersRated * numericKey;
    } else if (category?.mid?.includes(numericKey)) {
      totalMid += numberOfUsersRated;
      sumOfPrimaryRating += numberOfUsersRated * numericKey;
    } else if (category.top?.includes(numericKey)) {
      totalTop += numberOfUsersRated;
      sumOfPrimaryRating += numberOfUsersRated * numericKey;
    }
  }
  let rating: number;
  if (scalerRaterType === '0-10' || scalerRaterType === '1-5') {
    let npsVal: any = ((totalTop - totalBottom) / totalResponse) * 100;
    if (npsVal < 0 && (npsVal % 1 === -0.5 || npsVal % 1 === 0.5)) {
      rating = Math.floor(npsVal);
    } else if (npsVal > 0 && npsVal % 1 === 0.5) {
      rating = Math.ceil(npsVal);
    } else {
      rating = Math.round(npsVal);
    }
  } else {
    rating = parseFloat((sumOfPrimaryRating / totalResponse).toFixed(2));
  }
  return { rating, responses: totalResponse };
}
